import type { DateRange, IncidentItem, Meta } from '../../src/api/queries';
import { SOURCES, type Source } from '../../src/source';
import { addDays, type CoverageFact, coverageNote, sourceCoverage } from './coverage';
import { DEFAULT_VIEW_STATE } from './useUrlState';

/**
 * The chronicle's window arithmetic and its two-series merge (ux-spec §4).
 *
 * The one deliberate two-source surface on the site, and two-source at *item* grain: every row
 * carries the series it was read from, and the only figure derived across both is the item count.
 */

/** One row of the panel: the incident, and which of the two daily series it came out of. */
export interface ChronicleEntry {
  readonly item: IncidentItem;
  readonly source: Source;
}

/** Below this the day is too thin for a panel and the window reaches back (ux-spec §4). */
export const CHRONICLE_MIN_ITEMS = 8;

/** And no further back than this, whatever the count: it is a ööpäev, not an archive. */
export const CHRONICLE_MAX_EXTRA_DAYS = 2;

/** The last day one series has been read to. The runs are ascending, so it is the last one's edge;
 * `null` is a corpus nothing has been extracted from, which is a state and not a day. */
export function lastCoveredDay(coverage: CoverageFact): string | null {
  const last = coverage?.extracted?.at(-1);
  return last?.to ?? null;
}

/** The day the panel states: the later of the two series' own last covered days. The series that
 * has not reached it contributes no rows and says so through {@link chronicleNotes}. */
export function chronicleDay(meta: Meta | undefined): string | null {
  const days = SOURCES.map((source) => lastCoveredDay(sourceCoverage(meta, source))).filter(
    (day): day is string => day !== null,
  );
  return days.length === 0 ? null : days.reduce((a, b) => (a > b ? a : b));
}

/** The inclusive span `extraDays` before `day`, in the UTC calendar every date here is stated in. */
export function chronicleWindow(day: string, extraDays: number): DateRange {
  return { from: addDays(day, -extraDays), to: day };
}

/** Day, then clock, then id: a row whose paragraph stated no time gets an empty clock and settles
 * below its day's timed rows — the card's date-only fallback, made structural. */
function sortKey(entry: ChronicleEntry): string {
  return `${entry.item.effective_date} ${entry.item.occurred_time ?? ''} ${String(entry.item.id).padStart(12, '0')}`;
}

/** The two series' rows as one newest-first list, each row still carrying its own series. */
export function chronicleEntries(
  digest: readonly IncidentItem[],
  traffic: readonly IncidentItem[],
): ChronicleEntry[] {
  const entries: ChronicleEntry[] = [
    ...digest.map((item) => ({ item, source: 'digest' as const })),
    ...traffic.map((item) => ({ item, source: 'traffic' as const })),
  ];
  // Keyed once each: the key is ASCII, so collation would only add cost and locale sensitivity.
  return entries
    .map((entry) => ({ entry, key: sortKey(entry) }))
    .sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0))
    .map(({ entry }) => entry);
}

/** The window the panel settled on, and the rows inside it. */
export interface ChronicleWindow {
  window: DateRange;
  entries: ChronicleEntry[];
}

/** The narrowest window clearing {@link CHRONICLE_MIN_ITEMS}, capped at
 * {@link CHRONICLE_MAX_EXTRA_DAYS}. Rows outside it are dropped: the header names a day. */
export function narrowToWindow(entries: readonly ChronicleEntry[], day: string): ChronicleWindow {
  let widest: ChronicleWindow = { window: chronicleWindow(day, 0), entries: [] };
  for (let extra = 0; extra <= CHRONICLE_MAX_EXTRA_DAYS; extra++) {
    const window = chronicleWindow(day, extra);
    const inside = entries.filter(
      (e) => e.item.effective_date >= window.from && e.item.effective_date <= window.to,
    );
    widest = { window, entries: inside };
    if (inside.length >= CHRONICLE_MIN_ITEMS) return widest;
  }
  return widest;
}

/** Strictly older than yesterday: a corpus read up to yesterday is caught up, not lagging. */
export function isDayBehind(day: string, today: string): boolean {
  return day < addDays(today, -1);
}

/** One series' own sentence about the panel's window, when that series owes the reader one. */
export interface ChronicleNote {
  readonly source: Source;
  readonly note: string;
}

/** What each series owes the reader here, in `coverage.ts`'s words: the day is the later of the two
 * covered days, so the other's silence must read as a backlog and never as a quiet night. */
export function chronicleNotes(meta: Meta | undefined, window: DateRange): ChronicleNote[] {
  const state = { ...DEFAULT_VIEW_STATE, from: window.from, to: window.to };
  return SOURCES.flatMap((source) => {
    const note = coverageNote(state, sourceCoverage(meta, source));
    return note === null ? [] : [{ source, note }];
  });
}

/** Whether a page holds every row of the window, so the count badge may be a count. Under
 * `date_desc` its oldest row must pass the left edge — landing on it leaves that day split. */
export function isExhaustive(
  page: { items: readonly IncidentItem[]; next_cursor: string | null },
  from: string,
): boolean {
  if (page.next_cursor === null) return true;
  const oldest = page.items.at(-1);
  return oldest !== undefined && oldest.effective_date < from;
}
